import React, { useEffect, useState } from 'react';
import { publicApi } from '../utils/api';
import { formatDuration, formatDurationShort } from '../utils/duration';

/**
 * <PlanDuration planId="…" /> renders the formatted duration for the given plan.
 * If `planSlug` is given (e.g. "basic"), looks up by name match too.
 * Falls back to children/placeholder until plans are loaded.
 *
 * Usage:
 *   <PlanDuration planId={plan.id} />
 *   <PlanDuration planId={plan.id} variant="short" />
 *   <PlanDuration days={2192} />   // direct override
 */
let _cache = null;
let _inflight = null;

async function getPlans() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await publicApi.get('/api/subscriptions/plans');
      _cache = data?.plans || [];
      return _cache;
    } catch (err) {
      _cache = [];
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function clearPlanDurationCache() {
  _cache = null;
}

export function PlanDuration({ planId, planName, days, variant = 'long', className = '', placeholder = '—' }) {
  const [resolvedDays, setResolvedDays] = useState(days);

  useEffect(() => {
    if (typeof days === 'number') {
      setResolvedDays(days);
      return;
    }
    if (!planId && !planName) return;
    let cancelled = false;
    (async () => {
      const plans = await getPlans();
      const found = plans.find(p =>
        (planId && p.id === planId) ||
        (planName && p.name?.toLowerCase() === planName.toLowerCase())
      );
      if (!cancelled) setResolvedDays(found?.duration_days);
    })();
    return () => { cancelled = true; };
  }, [planId, planName, days]);

  if (resolvedDays === undefined || resolvedDays === null) {
    return <span className={className}>{placeholder}</span>;
  }
  const formatted = variant === 'short' ? formatDurationShort(resolvedDays) : formatDuration(resolvedDays);
  return <span className={className} data-testid="plan-duration">{formatted}</span>;
}

export default PlanDuration;
